<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  META_STANDARD_EVENTS,
  CUSTOM_EVENT_VALUE,
  DONT_SEND_VALUE,
} from 'dashboard/constants/metaConversionEvents';

// Fixed key for the conversation custom attribute this page auto-creates the first time an
// account configures Conversions API without already having a numeric/currency conversation
// attribute to hold the transaction value. Kept ASCII/stable regardless of locale so it never
// drifts from a slugified display name.
const DEFAULT_PURCHASE_VALUE_ATTRIBUTE_KEY = 'meta_conversion_value';
const VALUE_ATTRIBUTE_DISPLAY_TYPES = ['number', 'currency'];

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const labels = useMapGetter('labels/getLabels');
const conversationAttributes = useMapGetter('attributes/getConversationAttributes');

const enabled = ref(false);
const datasetId = ref('');
const testEventCode = ref('');
const pageId = ref('');
const purchaseCurrency = ref('');
const purchaseValueAttributeKey = ref('');
const accessToken = ref('');
const isSaving = ref(false);
const isCreatingDefaultAttribute = ref(false);
const tokenUpdated = ref(false);
const mapping = reactive({});

const valueAttributeOptions = computed(() =>
  conversationAttributes.value
    .filter(attribute =>
      VALUE_ATTRIBUTE_DISPLAY_TYPES.includes(attribute.attributeDisplayType)
    )
    .map(attribute => ({
      value: attribute.attributeKey,
      label: attribute.attributeDisplayName,
    }))
);

// If the account has no numeric/currency conversation attribute yet, create the default one so
// the field picker below is never a dead end -- matches this page's "just works" defaults for
// everything else (dataset id, currency, etc).
const ensureDefaultValueAttribute = async () => {
  if (valueAttributeOptions.value.length || isCreatingDefaultAttribute.value) return;

  isCreatingDefaultAttribute.value = true;
  try {
    await store.dispatch('attributes/create', {
      attribute_display_name: t(
        'INBOX_MGMT.CONVERSIONS.PURCHASE_VALUE.DEFAULT_ATTRIBUTE_NAME'
      ),
      attribute_key: DEFAULT_PURCHASE_VALUE_ATTRIBUTE_KEY,
      attribute_model: 'conversation_attribute',
      attribute_display_type: 'currency',
    });
    if (!purchaseValueAttributeKey.value) {
      purchaseValueAttributeKey.value = DEFAULT_PURCHASE_VALUE_ATTRIBUTE_KEY;
    }
  } catch {
    // Attribute key already exists (created from another inbox's page in the same account) or
    // the request failed -- either way, leave the picker for the user to sort out manually.
  } finally {
    isCreatingDefaultAttribute.value = false;
  }
};

onMounted(async () => {
  await store.dispatch('attributes/get');
  ensureDefaultValueAttribute();
});

const isTokenConfigured = computed(
  () => tokenUpdated.value || Boolean(props.inbox.conversion_api_access_token_configured)
);

const eventOptions = computed(() => [
  { value: DONT_SEND_VALUE, label: t('INBOX_MGMT.CONVERSIONS.EVENTS.DONT_SEND') },
  ...META_STANDARD_EVENTS.map(event => ({ value: event, label: event })),
  { value: CUSTOM_EVENT_VALUE, label: t('INBOX_MGMT.CONVERSIONS.EVENTS.CUSTOM') },
]);

const loadFromInbox = () => {
  const config = props.inbox.conversion_api_config || {};
  enabled.value = Boolean(props.inbox.conversion_api_enabled);
  datasetId.value = config.dataset_id || '';
  testEventCode.value = config.test_event_code || '';
  pageId.value = config.page_id || '';
  purchaseCurrency.value = config.purchase_currency || '';
  purchaseValueAttributeKey.value = config.purchase_value_attribute_key || '';
  accessToken.value = '';
  tokenUpdated.value = false;
  Object.keys(mapping).forEach(key => delete mapping[key]);
  Object.assign(mapping, config.label_event_mapping || {});
};

watch(() => props.inbox.id, loadFromInbox, { immediate: true });

const selectValueFor = labelTitle => {
  const value = mapping[labelTitle] || '';
  if (!value) return DONT_SEND_VALUE;
  return META_STANDARD_EVENTS.includes(value) ? value : CUSTOM_EVENT_VALUE;
};

const isCustom = labelTitle => selectValueFor(labelTitle) === CUSTOM_EVENT_VALUE;

const onEventSelect = (labelTitle, value) => {
  mapping[labelTitle] = value === CUSTOM_EVENT_VALUE ? '' : value;
};

const onCustomInput = (labelTitle, value) => {
  mapping[labelTitle] = value;
};

const save = async () => {
  isSaving.value = true;
  try {
    const labelEventMapping = Object.fromEntries(
      Object.entries(mapping).filter(([, value]) => Boolean(value))
    );
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      conversion_api_enabled: enabled.value,
      conversion_api_config: {
        dataset_id: datasetId.value,
        test_event_code: testEventCode.value,
        page_id: pageId.value,
        purchase_currency: purchaseCurrency.value,
        purchase_value_attribute_key: purchaseValueAttributeKey.value,
        label_event_mapping: labelEventMapping,
      },
      ...(accessToken.value ? { conversion_api_access_token: accessToken.value } : {}),
    });
    if (accessToken.value) {
      accessToken.value = '';
      tokenUpdated.value = true;
    }
    useAlert(t('INBOX_MGMT.CONVERSIONS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(
      error.response?.data?.message || t('INBOX_MGMT.CONVERSIONS.SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <SettingsToggleSection
      :header="t('INBOX_MGMT.CONVERSIONS.ENABLE.LABEL')"
      :description="t('INBOX_MGMT.CONVERSIONS.ENABLE.HELP')"
      :model-value="enabled"
      @update:model-value="value => (enabled = value)"
    />

    <SettingsFieldSection
      :label="t('INBOX_MGMT.CONVERSIONS.DATASET_ID.LABEL')"
      :help-text="t('INBOX_MGMT.CONVERSIONS.DATASET_ID.HELP')"
    >
      <woot-input
        v-model="datasetId"
        :placeholder="t('INBOX_MGMT.CONVERSIONS.DATASET_ID.PLACEHOLDER')"
      />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="t('INBOX_MGMT.CONVERSIONS.ACCESS_TOKEN.LABEL')"
      :help-text="t('INBOX_MGMT.CONVERSIONS.ACCESS_TOKEN.HELP')"
    >
      <div class="flex flex-col gap-2">
        <div
          v-if="isTokenConfigured"
          class="inline-flex w-fit items-center gap-1.5 rounded-md bg-n-alpha-2 px-2 py-1 text-label-small text-n-teal-11"
        >
          <span class="size-1.5 rounded-full bg-n-teal-9" />
          {{ t('INBOX_MGMT.CONVERSIONS.ACCESS_TOKEN.CONFIGURED') }}
        </div>
        <woot-input
          v-model="accessToken"
          type="password"
          :placeholder="t('INBOX_MGMT.CONVERSIONS.ACCESS_TOKEN.PLACEHOLDER')"
        />
      </div>
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="t('INBOX_MGMT.CONVERSIONS.TEST_EVENT_CODE.LABEL')"
      :help-text="t('INBOX_MGMT.CONVERSIONS.TEST_EVENT_CODE.HELP')"
    >
      <woot-input
        v-model="testEventCode"
        :placeholder="t('INBOX_MGMT.CONVERSIONS.TEST_EVENT_CODE.PLACEHOLDER')"
      />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="t('INBOX_MGMT.CONVERSIONS.PAGE_ID.LABEL')"
      :help-text="t('INBOX_MGMT.CONVERSIONS.PAGE_ID.HELP')"
    >
      <woot-input
        v-model="pageId"
        :placeholder="t('INBOX_MGMT.CONVERSIONS.PAGE_ID.PLACEHOLDER')"
      />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="t('INBOX_MGMT.CONVERSIONS.PURCHASE_CURRENCY.LABEL')"
      :help-text="t('INBOX_MGMT.CONVERSIONS.PURCHASE_CURRENCY.HELP')"
    >
      <woot-input
        v-model="purchaseCurrency"
        :placeholder="t('INBOX_MGMT.CONVERSIONS.PURCHASE_CURRENCY.PLACEHOLDER')"
      />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="t('INBOX_MGMT.CONVERSIONS.PURCHASE_VALUE.LABEL')"
      :help-text="t('INBOX_MGMT.CONVERSIONS.PURCHASE_VALUE.HELP')"
    >
      <SelectInput
        v-if="valueAttributeOptions.length"
        class="w-56"
        :model-value="purchaseValueAttributeKey"
        :options="valueAttributeOptions"
        @update:model-value="value => (purchaseValueAttributeKey = value)"
      />
      <p v-else class="text-sm text-n-slate-11">
        {{
          isCreatingDefaultAttribute
            ? t('INBOX_MGMT.CONVERSIONS.PURCHASE_VALUE.CREATING_ATTRIBUTE')
            : t('INBOX_MGMT.CONVERSIONS.PURCHASE_VALUE.NO_ATTRIBUTES')
        }}
      </p>
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="t('INBOX_MGMT.CONVERSIONS.LABEL_MAPPING.TITLE')"
      :help-text="t('INBOX_MGMT.CONVERSIONS.LABEL_MAPPING.HELP')"
    >
      <div v-if="labels.length" class="flex flex-col gap-3">
        <div
          v-for="label in labels"
          :key="label.id"
          class="flex flex-wrap items-center gap-3"
        >
          <span class="w-40 shrink-0 truncate text-sm text-n-slate-12">
            {{ label.title }}
          </span>
          <SelectInput
            class="w-56"
            :model-value="selectValueFor(label.title)"
            :options="eventOptions"
            @update:model-value="value => onEventSelect(label.title, value)"
          />
          <woot-input
            v-if="isCustom(label.title)"
            class="w-56 [&>input]:!mb-0"
            :model-value="mapping[label.title]"
            :placeholder="t('INBOX_MGMT.CONVERSIONS.EVENTS.CUSTOM_PLACEHOLDER')"
            @update:model-value="value => onCustomInput(label.title, value)"
          />
        </div>
      </div>
      <p v-else class="text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.CONVERSIONS.LABEL_MAPPING.EMPTY') }}
      </p>
    </SettingsFieldSection>

    <div class="flex justify-end">
      <NextButton :is-loading="isSaving" @click="save">
        {{ t('INBOX_MGMT.CONVERSIONS.SAVE') }}
      </NextButton>
    </div>
  </div>
</template>
